import { NextApiRequest, NextApiResponse } from 'next';
import GqlClient from '../clients/GqlClient';
import asyncLogger from '../logger';
import absoluteUrl from 'next-absolute-url';
import { CACHE_HOST, CACHE_PORT } from '../helpers/cluster';
import Redis from 'ioredis';
import uuid from './uuid';
import { getClientIp } from 'request-ip';

export const COOKIE_DEVICE_ID_NAME = 'deviceId';
export const COOKIE_TRACKING_SESSION_ID_NAME = 'trackingSessionId';
const COOKIE_TRACKING_SESSION_ID_TTL_SEC =
    +(process.env.COOKIE_TRACKING_SESSION_ID_TTL_SEC || 30 * 60);
const SET_COOKIE_HEADER_NAME = 'set-cookie';

type ParsedSetCookie =
    Record<string, { value: string; path?: string; expires?: string; }>;

export default class Tracking {
    private readonly redis: Redis;

    public constructor() {
        this.redis = new Redis(CACHE_PORT, CACHE_HOST);
    }

    public static async trackRequest(req: NextApiRequest): Promise<void> {
        const apiPkg = require('../../package.json');
        const url = absoluteUrl(req).origin + (req?.url || '');

        await GqlClient.gqlRequest(
            `mutation WSTrackRequest($input: TrackingRequestInput!) {
                trackRequest(input: $input)
            }`,
            {
                req,
                variables: { input: {
                    clientDate: req.headers['x-client-date'],
                    appVersion: apiPkg.version,
                    httpMethod: req.method,
                    url,
                } },
            },
        ).catch(asyncLogger.error);
    }

    private static updateRequestCookie(
        req: NextApiRequest,
        cookieName: string,
        cookieValue: string,
    ) {
        req.cookies[cookieName] = cookieValue;

        const headerCookies = req.headers.cookie
            ? req.headers.cookie
                .split(';')
                .map(c => c.trim())
                .reduce<Record<string, string>>((acc, c) => {
                    const [name, value] = c.split('=');
                    return { ...acc, [name]: value };
                }, {})
            : {};

        headerCookies[cookieName] = cookieValue;
        req.headers.cookie = Object.entries(headerCookies)
            .map(([name, value]) => `${ name }=${ value }`)
            .join('; ');
    }

    private async initCookie({
        req,
        redisKey,
        ttlSec,
        cookieName,
    }: {
        req: NextApiRequest,
        redisKey: string,
        ttlSec: number,
        cookieName: string,
    }): Promise<string> {
        const redisValue = await this.redis.get(redisKey);
        const cookieValue = req.cookies[cookieName];
        let value = cookieValue;

        if (cookieValue) {
            if (redisValue && redisValue !== cookieValue) {
                value = redisValue;
                Tracking.updateRequestCookie(req, cookieName, redisValue);
            }
        } else {
            if (redisValue) {
                value = redisValue;
            } else {
                value = uuid();
                await this.redis.set(redisKey, value, 'EX', ttlSec);
            }

            Tracking.updateRequestCookie(req, cookieName, value);
        }

        return value!;
    }

    private async initDeviceId(
        req: NextApiRequest,
    ): Promise<{ deviceId: string }> {
        const redisKey = `${
            COOKIE_DEVICE_ID_NAME
        }:${
            getClientIp(req)
        }:${
            req.headers['user-agent']
        }`;

        const deviceId = await this.initCookie({
            req,
            redisKey,
            ttlSec: 60,
            cookieName: COOKIE_DEVICE_ID_NAME,
        });

        return { deviceId };
    }

    private static getSessionRedisKey(deviceId: string): string {
        return `${ COOKIE_TRACKING_SESSION_ID_NAME }:${ deviceId }`;
    }

    public async initSession(
        req: NextApiRequest,
    ): Promise<{ deviceId: string; sessionId: string }> {
        const { deviceId } = await this.initDeviceId(req);
        const redisKey = Tracking.getSessionRedisKey(deviceId);

        const sessionId = await this.initCookie({
            req,
            redisKey,
            ttlSec: COOKIE_TRACKING_SESSION_ID_TTL_SEC,
            cookieName: COOKIE_TRACKING_SESSION_ID_NAME,
        });

        return { deviceId, sessionId };
    }

    private static async updateDeviceIdCookie(
        req: NextApiRequest,
        res: NextApiResponse,
    ): Promise<void> {
        // should be set in the 'initDeviceId' method
        const deviceId = req.cookies[COOKIE_DEVICE_ID_NAME]!;

        // updates the deviceId cookie TTL
        // ! dont't set HttpOnly flag for deviceId cookie
        //   because it is used in client side
        res.appendHeader(
            SET_COOKIE_HEADER_NAME,
            `${
                COOKIE_DEVICE_ID_NAME
            }=${
                deviceId
            }; Path=/; Expires=Fri, 31 Dec 9999 23:59:59 GMT;`,
        );
    }

    private static parseSetCookieHeader(res: NextApiResponse): ParsedSetCookie {
        const setCookieHeader = res.getHeader(SET_COOKIE_HEADER_NAME);

        if (
            !setCookieHeader ||
            !Array.isArray(setCookieHeader) ||
            !setCookieHeader.length
        ) {
            return {};
        }

        return setCookieHeader
            .reduce<ParsedSetCookie>((acc, s) => {
                if (!s) {
                    return acc;
                }

                const pairs = s.split(';').map(p => p.trim());
                const [name, value] = pairs[0].split('=');
                const attrs = pairs.length > 1
                    ? pairs.slice(1).reduce((acc, p) => {
                        const [key, val] = p.split('=');
                        return { ...acc, [key.toLowerCase()]: val || '' };
                    }, {})
                    : {};

                return { ...acc, [name]: { value: value || '', ...attrs } };
            }, {});
    }

    private async updateSessionCookie(
        req: NextApiRequest,
        res: NextApiResponse,
    ): Promise<void> {
        const deviceId = req.cookies[COOKIE_DEVICE_ID_NAME]!;
        const redisKey = Tracking.getSessionRedisKey(deviceId);
        const setCookies = Tracking.parseSetCookieHeader(res);
        const setCookie = setCookies[COOKIE_TRACKING_SESSION_ID_NAME];

        // should be set in the 'initSession' method
        const cookieValue = req.cookies[COOKIE_TRACKING_SESSION_ID_NAME]!;
        let value: string | undefined;

        if (setCookie) {
            if (
                !setCookie?.value ||
                (
                    !!setCookie.expires &&
                    new Date(setCookie.expires) < new Date()
                )
            ) {
                await this.redis.del(redisKey);
                return;
            } else {
                value = setCookie.value;
                this.redis.set(
                    redisKey,
                    value,
                    'EX',
                    COOKIE_TRACKING_SESSION_ID_TTL_SEC,
                );
            }
        } else {
            value = await this.redis.get(redisKey) || cookieValue;
        }

        res.appendHeader(
            SET_COOKIE_HEADER_NAME,
            `${
                COOKIE_TRACKING_SESSION_ID_NAME
            }=${
                value
            }; Path=/; Expires=${
                new Date(Date.now() +COOKIE_TRACKING_SESSION_ID_TTL_SEC * 1000)
                    .toUTCString()
            }; HttpOnly;`,
        );
    }

    public async updateCookies(
        req: NextApiRequest,
        res: NextApiResponse,
    ): Promise<void> {
        await Tracking.updateDeviceIdCookie(req, res);
        await this.updateSessionCookie(req, res);
    }
}

const scope: { tracking?: Tracking } = (
    typeof window === 'undefined' ? global : window
) as any;

if (!scope.tracking) {
    scope.tracking = new Tracking();
}

const tracking = scope.tracking;

export { tracking };
